import { useState } from 'react'
import { Menu } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { SidebarContent } from '@/components/Sidebar'
import ModulesList from './components/ModulesList'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { Button } from '@/components/ui/button'

type TabType = 'all' | 'recommended'

export default function LearningPathPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  return (
    <div className='min-h-screen w-full bg-[#EDEFF3]'>
      <div className='p-[27px]'>
        <div className='bg-white rounded-[24px] min-h-[calc(100vh-54px)]'>
          {/* Header */}
          <div className='px-8 py-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='ghost' size='icon' className='lg:hidden -ml-2'>
                    <Menu className='h-6 w-6' />
                    <span className='sr-only'>Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side='left' className='w-[280px] p-0'>
                  <SidebarContent organizationName={user?.coupleOrganizationName || 'IRIS Dashboard'} />
                </SheetContent>
              </Sheet>
              <h1 className='text-[40px] font-bold text-[#151515] leading-[70px]'>Learning Path</h1>
            </div>
            <div className='flex items-center gap-4 lg:gap-8'>
              <div className='flex items-center gap-3'>
                <div className='w-[48px] h-[47px] bg-[#434343] rounded-full flex items-center justify-center'>
                  <span className='text-[16px] font-bold text-white'>{initials}</span>
                </div>
                <span className='text-[24px] font-bold text-[#353535] hidden sm:block'>{displayName}</span>
              </div>
              <div className='w-[41px] h-[39px] bg-[#F7F8FA] rounded-full flex items-center justify-center cursor-pointer'>
                <svg width='4' height='18' viewBox='0 0 4 18' fill='#434343'>
                  <circle cx='2' cy='2' r='2' />
                  <circle cx='2' cy='9' r='2' />
                  <circle cx='2' cy='16' r='2' />
                </svg>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className='px-8 pb-8'>
            {/* Tabs */}
            <div className='flex gap-6 mb-4'>
              <button
                onClick={() => setActiveTab('all')}
                className={`pb-2 text-[16px] font-bold transition-colors relative ${
                  activeTab === 'all' ? 'text-[#151515]' : 'text-[#A0A0A0]'
                }`}
              >
                All Modules
                {activeTab === 'all' && (
                  <div className='absolute bottom-0 left-0 right-0 h-[3px] bg-[#FA6017] rounded-full' />
                )}
              </button>
              <button
                onClick={() => setActiveTab('recommended')}
                className={`pb-2 text-[16px] font-bold transition-colors relative ${
                  activeTab === 'recommended' ? 'text-[#151515]' : 'text-[#A0A0A0]'
                }`}
              >
                Recommended Modules
                {activeTab === 'recommended' && (
                  <div className='absolute bottom-0 left-0 right-0 h-[3px] bg-[#FA6017] rounded-full' />
                )}
              </button>
            </div>

            {/* Description */}
            <p className='text-[14px] font-normal text-[#4e4d4d] leading-[22px] mb-8 max-w-[900px]'>
              Based on your results, these learning modules have been chosen to support your
              growth as a couple. Each module includes a short video and a reflection activity.
              Take them at your own pace — every step you complete helps strengthen your
              marriage.
            </p>

            {/* Modules List */}
            <ModulesList activeTab={activeTab} />
          </div>
        </div>
      </div>
    </div>
  )
}
